import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, SwitchField } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Seller } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

interface CreditCardDescriptorType {
    id: ID;
    statementDescriptor: string;
    requireAdditionalBillingInfo: boolean;
}

const getInitialValues = (seller: Seller): CreditCardDescriptorType => {
    return {
        id: seller.id,
        statementDescriptor: seller.statementDescriptor,
        requireAdditionalBillingInfo: seller.preferences.payment.requireAdditionalBillingInfo,
    };
};

export const CreditCardDescriptor = () => {
    const seller = useSeller();
    const [isEditable, setIsEditable] = useState(false);
    const invalidate = useInvalidate();

    const { mutateAsync: save, isLoading } = useMutation(async (payload: CreditCardDescriptorType) => {
        await api.put(`/sellers/${seller.id}`, payload);
        invalidate("sellers", seller.id);
        flash.success("Successfully updated credit card settings");
    });

    const handleSubmit = async (values: CreditCardDescriptorType) => {
        await savePreference(values.requireAdditionalBillingInfo);
        await save(values);
        invalidate("sellers", seller.id);
        setIsEditable(false);
    };

    const { mutateAsync: savePreference } = useMutation(async (requireAdditionalBillingInfo: boolean) => {
        await api.post(`/users/${seller.id}/preferences/payment`, {
            requireAdditionalBillingInfo,
        });
    });

    const readOnly = !isEditable && !isLoading;

    return (
        <XolaFormik
            initialValues={getInitialValues(seller)}
            onSubmit={handleSubmit}
            onReset={() => setIsEditable(false)}
        >
            {({ values, setFieldValue, handleSubmit, handleReset }: any) => (
                <div className="space-y-4">
                    <FormField name="requireAdditionalBillingInfo" label="Additional Billing Information">
                        <SwitchField
                            isChecked={values.requireAdditionalBillingInfo}
                            onChange={() =>
                                setFieldValue("requireAdditionalBillingInfo", !values.requireAdditionalBillingInfo)
                            }
                        >
                            Require full address to process credit card payments
                        </SwitchField>
                    </FormField>

                    <FormField name="statementDescriptor" label="Descriptor">
                        <span className="flex space-x-2">
                            <InputField maxLength={17} disabled={readOnly} />
                            <SecondaryButton onClick={() => setIsEditable(true)}>Edit</SecondaryButton>
                        </span>
                    </FormField>

                    <div>
                        <SubmitButton type="button" isLoading={isLoading} onClick={handleSubmit}>
                            Save
                        </SubmitButton>

                        <SecondaryButton type="reset" className="ml-3" onClick={handleReset}>
                            Cancel
                        </SecondaryButton>
                    </div>
                </div>
            )}
        </XolaFormik>
    );
};
